/**
 * Caretakers Service
 * All caretaker-related database operations using Supabase.
 */

import { supabase } from '../../utils/supabase_config';
import { SupabaseService } from './supabaseService';
import { TABLES } from './tables';

export class CaretakersService extends SupabaseService {
  /**
   * Get all caretakers (active + deactivated).
   */
  static async getAll() {
    return super.getAll(TABLES.CARETAKERS);
  }

  /**
   * Create a new caretaker record.
   * @param {Object} data - { name, email, pay, color, clients }
   * @returns {Promise<string>} New row id
   */
  static async createCaretaker(data) {
    return this.create(TABLES.CARETAKERS, {
      name: data.name,
      email: data.email,
      pay: data.pay ?? { '0': 0 },
      color: data.color ?? '#000000',
      clients: data.clients ?? [],
      is_deactivated: false
    });
  }

  /**
   * Update caretaker fields (name, email, color, clients, etc.).
   * @param {string} caretakerId
   * @param {Object} updates - snake_case fields
   */
  static async updateCaretaker(caretakerId, updates) {
    return this.update(TABLES.CARETAKERS, caretakerId, updates);
  }

  /**
   * Update the pay rate for a caretaker.
   * Preserves the historical pay object structure: key "0" = current rate,
   * key = epoch ms string = historical rate at that time.
   * @param {string} caretakerId
   * @param {number} newPay
   * @param {number} effectiveDate - epoch ms timestamp (Date.getTime())
   */
  static async updatePay(caretakerId, newPay, effectiveDate) {
    try {
      // Fetch current pay object
      const { data, error } = await supabase
        .from(TABLES.CARETAKERS)
        .select('pay')
        .eq('id', caretakerId)
        .single();
      if (error) throw error;

      const payObj = { ...data.pay };
      const oldCurrentPay = payObj['0'];

      // Archive old rate under its epoch ms key
      payObj[String(effectiveDate)] = oldCurrentPay;
      // Set new current rate
      payObj['0'] = newPay;

      return this.update(TABLES.CARETAKERS, caretakerId, { pay: payObj });
    } catch (error) {
      this.handleError('Error updating caretaker pay', error);
      throw error;
    }
  }

  /**
   * Soft-deactivate a caretaker.
   * @param {string} caretakerId
   * @param {number} deactivationTime - epoch ms
   */
  static async deactivate(caretakerId, deactivationTime = Date.now()) {
    return this.update(TABLES.CARETAKERS, caretakerId, {
      is_deactivated: true,
      deactivation_time: deactivationTime
    });
  }

  /**
   * Restore a deactivated caretaker.
   * @param {string} caretakerId
   */
  static async restore(caretakerId) {
    return this.update(TABLES.CARETAKERS, caretakerId, {
      is_deactivated: false,
      deactivation_time: null
    });
  }

  /**
   * Hard-delete a caretaker record.
   * @param {string} caretakerId
   */
  static async deleteCaretaker(caretakerId) {
    return this.delete(TABLES.CARETAKERS, caretakerId);
  }

  /**
   * Update all shifts that reference this caretaker by name (cascade rename).
   * Called from EditCaretakersBox when a caretaker's name or color changes.
   * @param {string} oldName
   * @param {Object} updates - { caretaker, color, caretaker_email }
   */
  static async updateShiftsForCaretaker(oldName, updates) {
    try {
      const { error } = await supabase
        .from(TABLES.SHIFTS)
        .update(updates)
        .eq('caretaker', oldName);
      if (error) throw error;
    } catch (error) {
      this.handleError('Error cascading caretaker update to shifts', error);
      throw error;
    }
  }
}
